import {
  encodeMessage,
  decodeMessage,
  MessageType,
  type GameMessage,
  type InputUpdateMsg,
  type EntityUpdateMsg,
  type EntitySpawnMsg,
  type EntityDespawnMsg,
  type SectorDataMsg,
  type SectorChangeMsg,
  type DamageEventMsg,
  type DeathEventMsg,
  type ResourceCollectedMsg,
  type InventoryUpdateMsg,
  type HitConfirmMsg,
  type ChatReceiveMsg,
  type XPGainMsg,
  type LevelUpMsg,
  type PlayerStateMsg,
  type DockConfirmMsg,
  type MissionListMsg,
  type MissionProgressMsg,
  type MissionCompleteMsg,
  type MissionHistoryMsg,
  type MissionHistorySeedMsg,
  type PingMsg,
  type Vec3,
  type SectorCoord,
  type DiscoveredSectorsMsg,
  type SectorDiscoveredMsg,
  type WarpTravelResultMsg,
  type AuthResponseMsg,
  type PasswordResetResultMsg,
  type SavedCharacterMsg,
  type RespawnConfirmMsg,
  type EquipmentUpdateMsg,
  type ShipHangarUpdateMsg,
  type LeaderboardType,
  type StationShopSnapshotMsg,
  type SectorClaimsSnapshotMsg,
  type SectorClaimUpdateMsg,
  type SectorEventsSnapshotMsg,
  type SectorEventUpdateMsg,
  type SiegeUpdateMsg,
  type NpcItemDropMsg,
  type FriendsListDataMsg,
  type FriendRequestsDataMsg,
  type FriendStatusUpdateMsg,
  type FriendRequestReceivedMsg,
  type TradeResponseMsg,
  type MarketplaceListingsMsg,
  type MarketplaceResultMsg,
  type MarketplaceSoldMsg,
  type BountyBoardDataMsg,
  type AuthAttribution,
  type PlayerShipCosmetics,
} from '@viral-space/shared';
import { GameState } from '../game/state';
import type { InputState } from '../input/input-manager';

type MessageHandler = (msg: GameMessage) => void;

const INPUT_SEND_INTERVAL = 1000 / 20;
const PING_INTERVAL = 5000;
const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 30000;
const RECONNECT_BACKOFF = 1.5;

export class NetworkClient {
  private ws: WebSocket | null = null;
  private gameState: GameState;

  private connected = false;
  private authenticated = false;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private shouldReconnect = true;

  private lastInputSend = 0;
  private inputSeq = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private lastPingSent = 0;
  private _latency = 0;

  private handlers = new Map<MessageType, MessageHandler[]>();
  private disposed = false;

  private onConnectedCb?: () => void;
  private onDisconnectedCb?: (reason: string) => void;
  private _serverUrl = '';
  private _username = '';
  private _race = 0;
  private _shipClass = 0;
  private _language = '';

  constructor(gameState: GameState) {
    this.gameState = gameState;
    this.registerInternalHandlers();
  }

  private registerInternalHandlers(): void {
    this.on(MessageType.EntityUpdate, (msg) => {
      const m = msg as EntityUpdateMsg;
      this.gameState.processServerUpdate(m.entities);
    });
    this.on(MessageType.EntitySpawn, (msg) => {
      const m = msg as EntitySpawnMsg;
      this.gameState.processSpawn(m.entity);
    });
    this.on(MessageType.EntityDespawn, (msg) => {
      const m = msg as EntityDespawnMsg;
      this.gameState.processDespawn(m.entityId);
    });
    this.on(MessageType.SectorData, (msg) => {
      const m = msg as SectorDataMsg;
      this.gameState.setSectorData(m);
    });
    this.on(MessageType.SectorChange, (msg) => {
      const m = msg as SectorChangeMsg;
      this.gameState.handleSectorChange(m);
    });
    this.on(MessageType.DamageEvent, (msg) => {
      this.gameState.pushNotification({ type: 'damage', data: msg });
    });
    this.on(MessageType.DeathEvent, (msg) => {
      this.gameState.pushNotification({ type: 'death', data: msg });
    });
    this.on(MessageType.ResourceCollected, (msg) => {
      this.gameState.pushNotification({ type: 'resource', data: msg });
    });
    this.on(MessageType.ChatReceive, (msg) => {
      this.gameState.pushNotification({ type: 'chat', data: msg });
    });
    this.on(MessageType.XPGain, (msg) => {
      this.gameState.pushNotification({ type: 'xp', data: msg });
    });
    this.on(MessageType.LevelUp, (msg) => {
      this.gameState.pushNotification({ type: 'levelUp', data: msg });
    });
    this.on(MessageType.PlayerState, (msg) => {
      this.gameState.setPlayerProfile(msg as PlayerStateMsg);
    });
    this.on(MessageType.DockConfirm, (msg) => {
      this.gameState.pushNotification({ type: 'dockConfirm', data: msg });
    });
    this.on(MessageType.MissionList, (msg) => {
      const m = msg as MissionListMsg;
      // Server now sends two flavours over the same MessageType:
      //   scope='station' — catalogue at the docked station
      //   scope='log'     — the player's active mission log
      // Old payloads without `scope` are treated as catalogue (legacy
      // pre-rollout server). The journal HUD reads `getActiveMissions`
      // and re-renders on the missionList notification.
      if (m.scope === 'log') {
        this.gameState.setActiveMissions(m.missions);
      } else {
        this.gameState.setStationMissions(m.missions);
      }
      this.gameState.pushNotification({ type: 'missionList', data: msg });
    });
    this.on(MessageType.MissionProgress, (msg) => {
      const m = msg as MissionProgressMsg;
      this.gameState.applyMissionProgress(m.missionId, m.objectives);
      this.gameState.pushNotification({ type: 'missionProgress', data: msg });
    });
    this.on(MessageType.MissionComplete, (msg) => {
      const m = msg as MissionCompleteMsg;
      // Optimistic local insert into the journal mirror so the
      // "Completed" tab updates without waiting for the authoritative
      // MissionHistory push to round-trip. The snapshot is taken
      // BEFORE removing the active-mission row because that row is
      // gone by the time we render. The server's next history snapshot
      // (always sent immediately after a completion) reconciles by id.
      if (m.outcome === 'completed') {
        const snap = this.gameState.getActiveMission(m.missionId);
        if (snap) {
          this.gameState.recordCompletedMission({
            id: snap.id,
            title: snap.title,
            description: snap.description,
            isDaily: snap.isDaily,
            isWeekly: snap.isWeekly,
            rewards: m.rewards
              ? { xp: m.rewards.xp, credits: m.rewards.credits }
              : { xp: snap.rewards.xp, credits: snap.rewards.credits },
            completedAt: Date.now(),
          });
        }
      }
      this.gameState.removeActiveMission(m.missionId);
      this.gameState.pushNotification({ type: 'missionComplete', data: msg });
    });
    this.on(MessageType.MissionHistory, (msg) => {
      const m = msg as MissionHistoryMsg;
      this.gameState.setCompletedMissionHistory(m.entries);
      // First authoritative snapshot landed → drain any leftover
      // pre-server-history localStorage payload and ship it as a
      // one-shot seed. The server will only merge it when its own
      // history is empty, which is exactly the case for pilots whose
      // journal lived only in the browser before this migration.
      this.uploadLegacyCompletedMissionsSeed();
    });
    this.on(MessageType.Pong, () => {
      this._latency = performance.now() - this.lastPingSent;
    });
    this.on(MessageType.DiscoveredSectors, (msg) => {
      const m = msg as DiscoveredSectorsMsg;
      this.gameState.setDiscoveredSectors(m.sectors);
    });
    this.on(MessageType.SectorDiscovered, (msg) => {
      const m = msg as SectorDiscoveredMsg;
      this.gameState.addDiscoveredSector(m.sector);
    });
    this.on(MessageType.RespawnConfirm, (msg) => {
      this.gameState.pushNotification({ type: 'respawn', data: msg as RespawnConfirmMsg });
    });
    this.on(MessageType.EquipmentUpdate, (msg) => {
      this.gameState.setEquipment(msg as EquipmentUpdateMsg);
    });
    this.on(MessageType.ShipHangarUpdate, (msg) => {
      this.gameState.setHangar(msg as ShipHangarUpdateMsg);
    });
    this.on(MessageType.ActiveBuffsUpdate, (msg) => {
      this.gameState.pushNotification({ type: 'activeBuffs', data: msg });
    });
    this.on(MessageType.StationShopSnapshot, (msg) => {
      this.gameState.pushNotification({ type: 'stationShop', data: msg as StationShopSnapshotMsg });
    });
    this.on(MessageType.SectorClaimsSnapshot, (msg) => {
      const m = msg as SectorClaimsSnapshotMsg;
      this.gameState.applySectorClaimsSnapshot(m.claims);
    });
    this.on(MessageType.SectorClaimUpdate, (msg) => {
      const m = msg as SectorClaimUpdateMsg;
      this.gameState.applySectorClaimUpdate(m.claim);
    });
    this.on(MessageType.SiegeUpdate, (msg) => {
      this.gameState.applySiegeUpdate(msg as SiegeUpdateMsg);
    });
    this.on(MessageType.SectorEventsSnapshot, (msg) => {
      const m = msg as SectorEventsSnapshotMsg;
      this.gameState.applyActiveSectorEventsSnapshot(m.events);
    });
    this.on(MessageType.SectorEventUpdate, (msg) => {
      const m = msg as SectorEventUpdateMsg;
      this.gameState.applyActiveSectorEventUpdate({
        event: m.event,
        removedId: m.removedId,
      });
    });
    this.on(MessageType.NpcItemDrop, (msg) => {
      this.gameState.pushNotification({ type: 'npcItemDrop', data: msg as NpcItemDropMsg });
    });
    this.on(MessageType.FriendsListData, (msg) => {
      const m = msg as FriendsListDataMsg;
      this.gameState.setFriendsList(m.friends);
    });
    this.on(MessageType.FriendRequestsData, (msg) => {
      const m = msg as FriendRequestsDataMsg;
      this.gameState.setFriendRequests(m.incoming, m.outgoing);
    });
    this.on(MessageType.FriendStatusUpdate, (msg) => {
      const m = msg as FriendStatusUpdateMsg;
      this.gameState.applyFriendStatusUpdate(m);
      this.gameState.pushNotification({ type: 'friendStatus', data: m });
    });
    this.on(MessageType.FriendRequestReceived, (msg) => {
      this.gameState.pushNotification({
        type: 'friendRequest',
        data: msg as FriendRequestReceivedMsg,
      });
    });
    this.on(MessageType.TradeResponse, (msg) => {
      const m = msg as TradeResponseMsg;
      const notifType =
        m.outcome === 'received'
          ? 'tradeReceived'
          : m.outcome === 'sent'
            ? 'tradeSent'
            : 'tradeFailed';
      this.gameState.pushNotification({ type: notifType, data: m });
    });
    this.on(MessageType.MarketplaceListings, (msg) => {
      const m = msg as MarketplaceListingsMsg;
      this.gameState.setMarketplaceListings(m.scope, m.listings, m.serverTime);
    });
    this.on(MessageType.MarketplaceResult, (msg) => {
      this.gameState.pushNotification({
        type: 'marketplaceResult',
        data: msg as MarketplaceResultMsg,
      });
    });
    this.on(MessageType.MarketplaceSold, (msg) => {
      this.gameState.pushNotification({
        type: 'marketplaceSold',
        data: msg as MarketplaceSoldMsg,
      });
    });
    this.on(MessageType.BountyBoardData, (msg) => {
      const m = msg as BountyBoardDataMsg;
      this.gameState.setBountyContracts(m.contracts, m.serverTime);
      this.gameState.pushNotification({ type: 'bountyBoard', data: m });
    });
  }

  async authenticate(
    url: string,
    mode: 'login' | 'signup' | 'guest' | 'steam',
    username: string,
    password?: string,
    email?: string,
    utm?: AuthAttribution,
  ): Promise<{
    success: boolean;
    username: string;
    characters: SavedCharacterMsg[];
    /**
     * Server-issued session id. Doubles as the bearer token for the
     * billing HTTP endpoints (`x-billing-token`); the value is opaque
     * to the client.
     */
    playerId?: string;
    /**
     * Premium subscription snapshot taken at login. Optional for backward
     * compat with pre-premium servers (treated as `{ active: false }` by
     * the UI). The client uses this to gate premium-only race cards in
     * the character-creation screen; the server still re-checks
     * server-side on `SaveCharacter`, so a tampered value can't unlock
     * gameplay — only the visual state of the cards.
     */
    premium?: { active: boolean; currentPeriodEnd: number | null };
    /**
     * Account role from the server. Drives admin-only client surfaces
     * (perf / memory monitor, future debug overlays). Defaults to
     * `'player'` when the server omitted the field (older builds).
     */
    role?: 'admin' | 'player';
    error?: string;
  }> {
    this._serverUrl = url;
    return new Promise((resolve, reject) => {
      if (this.disposed) return reject(new Error('disposed'));
      this.cleanup();

      const ws = new WebSocket(url);
      ws.binaryType = 'arraybuffer';
      this.ws = ws;

      ws.onopen = () => {
        this.connected = true;
        this.reconnectAttempts = 0;
        // Gather Steam auth ticket asynchronously then send AuthRequest.
        // Non-Electron environments resolve immediately with nulls.
        void this.buildSteamAuth()
          .then(({ steamTicket, steamId }) => {
            // `steam` mode is credential-less — the server identifies the
            // account from the verified ticket alone. `guest` carries the
            // display name in `guestName`. Only login/signup send the
            // username + password pair.
            const wantsCreds = mode === 'login' || mode === 'signup';
            this.send({
              type: MessageType.AuthRequest,
              mode,
              username: wantsCreds ? username : undefined,
              password: wantsCreds ? password : undefined,
              email,
              guestName: mode === 'guest' ? username : undefined,
              // Server only persists `utm` for `mode === 'signup'` — see
              // `MongoPlayerStore.signup`. Forwarding on every connect is
              // a couple of extra string fields on the wire, but it keeps
              // the call site honest: the auth-screen flow doesn't have
              // to special-case attribution per mode.
              utm: utm ?? undefined,
              steamTicket: steamTicket ?? undefined,
              steamId: steamId ?? undefined,
            });
          })
          .catch((err: unknown) => {
            // If building/encoding the AuthRequest throws, the server never
            // receives anything and no AuthResponse will arrive — reject so
            // the login screen shows an error instead of spinning forever.
            this.cleanup();
            reject(err instanceof Error ? err : new Error(String(err)));
          });
      };

      ws.onmessage = (event: MessageEvent) => {
        if (!(event.data instanceof ArrayBuffer)) return;
        try {
          const decoded = decodeMessage(new Uint8Array(event.data));
          if (!decoded || decoded.type === undefined) return;

          if (decoded.type === MessageType.AuthResponse) {
            const resp = decoded as AuthResponseMsg;
            if (resp.success) {
              this.authenticated = true;
              this.startPing();
              resolve({
                success: true,
                username: resp.username ?? username,
                playerId: resp.playerId,
                characters: resp.characters ?? [],
                premium: resp.premium
                  ? {
                      active: !!resp.premium.active,
                      currentPeriodEnd: resp.premium.currentPeriodEnd ?? null,
                    }
                  : { active: false, currentPeriodEnd: null },
                role: resp.role === 'admin' ? 'admin' : 'player',
              });
            } else {
              this.cleanup();
              resolve({
                success: false,
                username,
                characters: [],
                error: resp.error ?? 'Authentication failed',
              });
            }
            return;
          }

          const rawType = decoded.type;
          const messageHandlers =
            this.handlers.get(rawType) ??
            (typeof rawType === 'string' || typeof rawType === 'number'
              ? this.handlers.get(Number(rawType) as MessageType)
              : undefined);
          if (messageHandlers) {
            for (const handler of messageHandlers) handler(decoded);
          }
        } catch {
          /* drop malformed */
        }
      };

      ws.onclose = (event: CloseEvent) => {
        const wasConnected = this.connected;
        this.connected = false;
        this.authenticated = false;
        this.stopPing();
        if (wasConnected) {
          this.onDisconnectedCb?.(event.reason || 'Connection lost');
        } else {
          // Close fired before we ever got `onopen` — the connect itself
          // failed. Surface the URL, close code, and reason so the caller
          // can display an actionable error instead of a blank "Connection
          // failed". WebSocket close code 1006 == abnormal close (e.g. TLS
          // failure, DNS failure, endpoint not serving a WS upgrade).
          reject(
            new Error(
              `WebSocket connect failed (code ${event.code}${event.reason ? `: ${event.reason}` : ''}) — URL: ${url}`,
            ),
          );
        }
      };

      ws.onerror = () => {
        // onerror is typically followed by onclose; defer the reject to
        // onclose so we can include the close code. If the close never
        // fires (shouldn't happen but guard anyway) also reject here with
        // the URL so the caller knows what was tried.
        if (this.ws === ws && !this.connected) {
          reject(new Error(`WebSocket connect failed — URL: ${url}`));
        }
      };
    });
  }

  /** Step 1 of the forgot-password flow: ask the server to email a code. */
  async requestPasswordReset(
    url: string,
    email: string,
  ): Promise<{ success: boolean; error?: string }> {
    return this.passwordResetRoundTrip(
      url,
      { type: MessageType.PasswordResetRequest, email },
      'request',
    );
  }

  /** Step 2: redeem the emailed code and set the new password. */
  async confirmPasswordReset(
    url: string,
    email: string,
    code: string,
    newPassword: string,
  ): Promise<{ success: boolean; error?: string }> {
    return this.passwordResetRoundTrip(
      url,
      { type: MessageType.PasswordResetConfirm, email, code, newPassword },
      'confirm',
    );
  }

  /**
   * One-shot request/response over a dedicated short-lived socket. The
   * reset flow runs from the auth screen BEFORE any authenticated
   * session exists, so we deliberately do not touch `this.ws` or any of
   * the reconnect / ping machinery — open, send, await the matching
   * {@link MessageType.PasswordResetResult}, close.
   */
  private passwordResetRoundTrip(
    url: string,
    msg: GameMessage,
    stage: 'request' | 'confirm',
  ): Promise<{ success: boolean; error?: string }> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const ws = new WebSocket(url);
      ws.binaryType = 'arraybuffer';

      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        try { ws.close(); } catch { /* already closed */ }
        fn();
      };
      const timeout = setTimeout(
        () => finish(() => reject(new Error('Password reset request timed out'))),
        15_000,
      );

      ws.onopen = () => ws.send(encodeMessage(msg));
      ws.onmessage = (event: MessageEvent) => {
        if (!(event.data instanceof ArrayBuffer)) return;
        try {
          const decoded = decodeMessage(new Uint8Array(event.data));
          if (decoded?.type !== MessageType.PasswordResetResult) return;
          const m = decoded as PasswordResetResultMsg;
          if (m.stage !== stage) return;
          finish(() => resolve({ success: m.success, error: m.error }));
        } catch {
          /* drop malformed */
        }
      };
      ws.onerror = () => finish(() => reject(new Error(`Connection failed — URL: ${url}`)));
      ws.onclose = () => finish(() => reject(new Error('Connection closed')));
    });
  }

  joinGame(
    username: string,
    race: number,
    language: string,
    shipClass?: number,
  ): void {
    this._username = username;
    this._race = race;
    this._language = language;
    if (shipClass !== undefined) this._shipClass = shipClass;
    this.shouldReconnect = true;
    this.send({
      type: MessageType.JoinGame,
      race,
      shipClass: this._shipClass,
      username,
      language,
    });
    this.onConnectedCb?.();
  }

  async connect(
    url: string,
    username: string,
    race: number,
    language: string,
    shipClass?: number,
  ): Promise<void> {
    this._serverUrl = url;
    this._username = username;
    this._race = race;
    this._language = language;
    if (shipClass !== undefined) this._shipClass = shipClass;
    return new Promise((resolve, reject) => {
      if (this.disposed) return reject(new Error('disposed'));
      this.shouldReconnect = true;

      if (this.authenticated && this.connected) {
        this.joinGame(username, race, language, this._shipClass);
        resolve();
        return;
      }

      this.cleanup();

      const ws = new WebSocket(url);
      ws.binaryType = 'arraybuffer';
      this.ws = ws;

      ws.onopen = () => {
        this.connected = true;
        this.reconnectAttempts = 0;
        this.send({
          type: MessageType.AuthRequest,
          mode: 'guest',
          guestName: username,
        });
        this.send({
          type: MessageType.JoinGame,
          race,
          shipClass: this._shipClass,
          username,
          language,
        });
        this.startPing();
        this.authenticated = true;
        this.onConnectedCb?.();
        resolve();
      };

      ws.onmessage = (event: MessageEvent) => {
        if (!(event.data instanceof ArrayBuffer)) return;
        try {
          const decoded = decodeMessage(new Uint8Array(event.data));
          if (!decoded || decoded.type === undefined) return;
          const rawType = decoded.type;
          const messageHandlers =
            this.handlers.get(rawType) ??
            (typeof rawType === 'string' || typeof rawType === 'number'
              ? this.handlers.get(Number(rawType) as MessageType)
              : undefined);
          if (messageHandlers) {
            for (const handler of messageHandlers) handler(decoded);
          }
        } catch {
          /* drop malformed */
        }
      };

      ws.onclose = (event: CloseEvent) => {
        const wasConnected = this.connected;
        this.connected = false;
        this.authenticated = false;
        this.stopPing();
        if (wasConnected) {
          this.onDisconnectedCb?.(event.reason || 'Connection lost');
        }
        if (this.shouldReconnect && !this.disposed) {
          this.scheduleReconnect(url, username, race, language);
        }
      };

      ws.onerror = () => {
        reject(new Error('WebSocket connection failed'));
      };
    });
  }

  saveCharacter(character: SavedCharacterMsg): void {
    this.send({
      type: MessageType.SaveCharacter,
      character,
    } as GameMessage);
  }

  /**
   * Ask the server to permanently delete the authenticated account and
   * all of its data. Caller is responsible for having confirmed the
   * irreversible-action warning with the player first. The server
   * replies with an {@link MessageType.AccountDeleted} and then closes
   * the socket.
   */
  sendDeleteAccount(): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.DeleteAccount } as GameMessage);
  }

  /**
   * One-shot migration: drain any legacy localStorage completed-mission
   * journal and forward it to the server. The server merges only when
   * its own persisted history for this pilot is empty, then echoes the
   * authoritative snapshot back via {@link MessageType.MissionHistory}.
   *
   * Safe to call repeatedly — the drainer empties localStorage on the
   * first read, so subsequent calls become no-ops automatically.
   */
  private uploadLegacyCompletedMissionsSeed(): void {
    const legacy = this.gameState.drainLegacyCompletedMissionsLocalStorage();
    if (legacy.length === 0) return;
    const seed: MissionHistorySeedMsg = {
      type: MessageType.MissionHistorySeed,
      entries: legacy,
    };
    this.send(seed as GameMessage);
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.cleanup();
  }

  /**
   * Register a callback invoked when an established connection drops
   * involuntarily (server crash, network failure, server-side close).
   *
   * Self-initiated disconnects (`disconnect()`) do NOT fire this callback
   * because `cleanup()` nulls the socket's `onclose` handler before
   * closing the WebSocket.
   *
   * The callback runs inside the `onclose` handler so callers can flip
   * `shouldReconnect` (via `disconnect()`) before the auto-reconnect
   * scheduler kicks in further down the same handler.
   */
  onDisconnected(cb: (reason: string) => void): void {
    this.onDisconnectedCb = cb;
  }

  private cleanup(): void {
    this.stopPing();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close();
      }
      this.ws = null;
    }
    this.connected = false;
    this.authenticated = false;
  }

  private scheduleReconnect(
    url: string,
    username: string,
    race: number,
    language: string,
  ): void {
    if (this.reconnectTimer !== null) return;
    const delay = Math.min(
      RECONNECT_BASE_DELAY *
        Math.pow(RECONNECT_BACKOFF, this.reconnectAttempts),
      RECONNECT_MAX_DELAY,
    );
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(url, username, race, language, this._shipClass).catch(() => {});
    }, delay);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this.connected) {
        this.lastPingSent = performance.now();
        this.send({ type: MessageType.Ping, clientTime: this.lastPingSent });
      }
    }, PING_INTERVAL);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private send(msg: GameMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(encodeMessage(msg));
  }

  sendInput(inputState: InputState): void {
    const now = performance.now();
    if (now - this.lastInputSend < INPUT_SEND_INTERVAL) return;
    this.lastInputSend = now;
    if (!this.authenticated) return;

    // Send absolute rotation from the player entity so the server
    // always has the correct orientation, regardless of frame-rate
    // differences or dropped packets.
    const player = this.gameState.getPlayerEntity();
    const rotation = player
      ? {
          x: player.transform.rotation.x,
          y: player.transform.rotation.y,
          z: player.transform.rotation.z,
        }
      : inputState.rotation;

    const msg: InputUpdateMsg = {
      type: MessageType.InputUpdate,
      seq: this.inputSeq++,
      thrust: inputState.thrust,
      rotation,
      isBoosting: inputState.isBoosting,
      isFiring: inputState.isFiring,
      weaponSlot: inputState.weaponSlot,
      timestamp: now,
    };
    this.send(msg);
  }

  sendFire(weaponSlot: number, targetDir: Vec3): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.FireWeapon, weaponSlot, targetDir });
  }

  sendChat(
    channel: number,
    content: string,
    opts?: { targetId?: string; targetName?: string },
  ): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.ChatSend,
      channel,
      content,
      targetId: opts?.targetId,
      targetName: opts?.targetName,
    });
  }

  sendDockRequest(stationId: string): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.DockRequest, stationId });
  }

  sendUndockRequest(): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.UndockRequest } as GameMessage);
  }

  sendWarpGateUse(gateId: string, destinationSector: SectorCoord): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.WarpGateUse,
      gateId,
      destinationSector,
    });
  }

  sendLandRequest(planetEntityId: string): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.LandRequest,
      planetEntityId,
    });
  }

  sendLiftoffRequest(): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.LiftoffRequest,
    });
  }

  /**
   * Hold-to-investigate at a surface ruin completed the 3 s timer
   * client-side. Server validates proximity + emits codex unlock.
   */
  sendInvestigateRuin(ruinId: string): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.InvestigateRuin,
      ruinId,
    });
  }

  sendSellItem(itemId: string, quantity: number, merchantId?: string): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.SellItem,
      itemId,
      quantity,
      merchantId,
    });
  }

  sendBuyItem(itemId: string, quantity = 1, merchantId?: string): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.BuyItem,
      itemId,
      quantity,
      merchantId,
    });
  }

  sendMerchantHail(merchantId: string): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.MerchantHail,
      merchantId,
    });
  }

  sendMerchantClose(merchantId: string): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.MerchantClose,
      merchantId,
    });
  }

  sendCraftItem(recipeId: string): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.CraftItem,
      recipeId,
    });
  }

  sendEquipItem(slot: 'weapon' | 'shield' | 'engine', itemId: string): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.EquipItem,
      slot,
      itemId,
    });
  }

  sendUseItem(itemId: string): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.UseItem,
      itemId,
    });
  }

  sendBuyShip(shipClass: number): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.BuyShip,
      shipClass,
    });
  }

  sendSwitchShip(shipClass: number): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.SwitchShip,
      shipClass,
    });
  }

  sendSetShipCosmetics(cosmetics: PlayerShipCosmetics): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.SetShipCosmetics,
      cosmetics,
    });
  }

  sendClaimSector(sectorKey: string): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.ClaimSector,
      sectorKey,
    });
  }

  sendReinforceGarrison(sectorKey: string, targetLevel: number): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.ReinforceGarrison,
      sectorKey,
      targetLevel,
    });
  }

  sendLeaderboardRequest(board: LeaderboardType, limit = 50): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.LeaderboardRequest,
      board,
      limit,
    });
  }

  sendTutorialAdvance(phase: 'movement' | 'mining' | 'combat' | 'done'): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.TutorialAdvance,
      phase,
    });
  }

  sendMissionAccept(missionId: string): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.MissionAccept, missionId });
  }

  sendMissionAbandon(missionId: string): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.MissionAbandon, missionId });
  }

  // ── Friends list ───────────────────────────────────────────────────

  sendFriendAdd(targetName: string): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.FriendAdd, targetName });
  }

  /** Pull the recent transcript for one chat thread (comms window). */
  sendChatHistoryRequest(
    channel: number,
    opts?: { partnerName?: string; limit?: number },
  ): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.ChatHistoryRequest,
      channel,
      partnerName: opts?.partnerName,
      limit: opts?.limit,
    });
  }

  /** Look up a pilot's public profile card by callsign. */
  sendPlayerProfileRequest(name: string): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.PlayerProfileRequest, name });
  }

  sendFriendAccept(requestId: string): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.FriendAccept, requestId });
  }

  sendFriendReject(requestId: string): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.FriendReject, requestId });
  }

  sendFriendRemove(friendKey: string): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.FriendRemove, friendKey });
  }

  // ── Player-to-player gifting ───────────────────────────────────────

  sendTradeRequest(recipientName: string, itemId: string, quantity: number): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.TradeRequest,
      recipientName,
      itemId,
      quantity,
    });
  }

  // ── Player marketplace ─────────────────────────────────────────────

  sendMarketplaceList(scope: 'all' | 'mine' = 'all'): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.MarketplaceList, scope });
  }

  sendMarketplaceCreate(itemId: string, quantity: number, pricePerUnit: number): void {
    if (!this.authenticated) return;
    this.send({
      type: MessageType.MarketplaceCreate,
      itemId,
      quantity,
      pricePerUnit,
    });
  }

  sendMarketplaceCancel(listingId: string): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.MarketplaceCancel, listingId });
  }

  sendMarketplaceBuy(listingId: string): void {
    if (!this.authenticated) return;
    this.send({ type: MessageType.MarketplaceBuy, listingId });
  }

  on(type: MessageType, handler: MessageHandler): () => void {
    const existing = this.handlers.get(type) ?? [];
    existing.push(handler);
    this.handlers.set(type, existing);
    return () => {
      const list = this.handlers.get(type);
      if (list) {
        const idx = list.indexOf(handler);
        if (idx >= 0) list.splice(idx, 1);
      }
    };
  }

  get latency(): number {
    return this._latency;
  }

  isConnected(): boolean {
    return this.connected && this.authenticated;
  }

  /**
   * Attempt to get a Steam auth session ticket + SteamID from the Electron
   * preload bridge. Returns null values when running in a web browser or
   * when Steam is not running. Never throws — failures are swallowed so
   * login still proceeds without Steam credentials.
   */
  private async buildSteamAuth(): Promise<{ steamTicket: string | null; steamId: string | null }> {
    const steam = (window as Window & typeof globalThis).zerionDesktop?.steam;
    if (!steam) return { steamTicket: null, steamId: null };
    try {
      const available = await steam.isAvailable();
      if (!available) return { steamTicket: null, steamId: null };
      const [steamTicket, steamId] = await Promise.all([
        steam.getAuthTicket(),
        steam.getSteamId(),
      ]);
      // Coerce both values to plain strings: the IPC bridge can deliver a
      // BigInt SteamID (structured clone supports it) and msgpack would
      // throw on it inside ws.onopen, killing the AuthRequest and leaving
      // the login screen hanging forever.
      return {
        steamTicket: steamTicket != null ? String(steamTicket) : null,
        steamId: steamId != null ? String(steamId) : null,
      };
    } catch {
      return { steamTicket: null, steamId: null };
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.disconnect();
    this.handlers.clear();
  }
}
